<template>
  <div>
    <div>
      <p class="truncate text-sm font-medium text-gray-500">CA Estaly</p>
      <p class="text-lg pl-0.5 mt-1 tracking-tight text-gray-700">
        {{ formatPrice(estalyTurnover?.current) }}
        <span v-if="estalyTurnover?.previous != null" class="text-sm ml-4 text-gray-500">
          {{ formatPrice(estalyTurnover?.previous) }}
          {{ comparisonType ? $t(comparisonType) : "" }}
        </span>
        <Variation class="ml-2 inline-flex" :variation="estalyTurnover?.variation" />
      </p>
    </div>

    <LineChart v-if="isDailyView" :data="dailyChartData" :options="chartOptions" :chart-height="170" />
    <LineChart v-else :data="monthlyChartData" :options="chartOptions" :chart-height="170" />
  </div>
</template>

<script>
import { LineChart, Variation, formatPrice } from "@estaly/ui";

export default {
  components: { LineChart, Variation },

  props: {
    analytics: { type: Object, required: true },
    chartView: { type: String, required: true },
    comparisonType: { type: String, default: "previousMonth" },
  },

  computed: {
    isDailyView() {
      return this.chartView === "daily";
    },

    estalyTurnover() {
      return this.analytics?.general?.estalyTurnover;
    },

    dailyChartData() {
      const daily = this.analytics?.sales?.dailyEstalyTurnover;
      if (!daily?.current) return {};

      const currentData = daily.current.map((entry) => ({ date: entry.date, value: Number(entry.count) || 0 }));
      let previousData = [];

      if (daily.previous) {
        previousData = currentData.map((entry, index) => ({
          date: entry.date,
          value: Number(daily.previous[index]?.count) || 0,
        }));
      }

      return {
        estaly_turnover: { color: "#8C1EFC", value: currentData },
        previous_estaly_turnover: { color: "#D1D5DB", value: previousData },
      };
    },

    monthlyChartData() {
      const monthly = this.analytics?.sales?.monthlyEstalyTurnover;
      const currentData =
        monthly?.current?.map((entry) => ({ date: entry.date, value: Number(entry.count) || 0 })) || [];

      return {
        estaly_turnover: { color: "#8C1EFC", value: currentData },
      };
    },

    activeChartData() {
      return this.isDailyView ? this.dailyChartData : this.monthlyChartData;
    },

    maxValue() {
      const datasets = Object.values(this.activeChartData);
      const allValues = datasets.flatMap((d) => (d.value || []).map((p) => p.value));
      return Math.max(1, ...allValues);
    },

    chartOptions() {
      const isDaily = this.isDailyView;
      const data = this.activeChartData;
      const maxVal = isDaily ? this.maxValue : this.maxValue * 1.15;

      return {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        scales: {
          x: {
            grid: { display: false },
            title: { display: true, text: isDaily ? "Date" : "Mois" },
            ticks: {
              maxRotation: 0,
              minRotation: 0,
              callback: (value, index, values) => {
                const step = isDaily ? 4 : 2;
                if (index === 0 || index === values.length - 1 || index % step === 0) {
                  return data["estaly_turnover"]?.value[index]?.date || value;
                }
                return "";
              },
            },
          },
          y: {
            grid: { display: false, drawBorder: false },
            beginAtZero: true,
            title: { display: true, text: "CA (€)" },
            ticks: {
              maxTicksLimit: 2,
              callback: (value) => {
                if (value === 0) return formatPrice(value);
                if (isDaily && value === this.maxValue) return formatPrice(value);
                if (!isDaily && Math.abs(value - this.maxValue * 1.15) < 1) return formatPrice(this.maxValue);
                return "";
              },
            },
            suggestedMax: maxVal,
            max: maxVal,
          },
        },
        plugins: {
          annotation: false,
          datalabels: false,
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (tooltipItem) => ` ${tooltipItem.dataset.label} : ${formatPrice(tooltipItem.raw.y)}`,
            },
          },
        },
      };
    },
  },

  methods: {
    formatPrice,
  },
};
</script>
